/**
 * Which brand domain this page was opened on, and the addresses that go with it.
 *
 * Production serves one build on several domains at once (tab1099.com and tab1098t.com). This
 * app lives under /efile/ on the portal's own address (tab1099.com/efile/), so the address bar
 * never leaves the brand domain; its API is `efile-api.<domain>`. The older `efile.<domain>`
 * address still works and signs out to the bare domain. A person who opened the app on one
 * brand stays on it, so the API it calls and the portal it signs out to are read from the
 * address bar, not baked in at build time.
 *
 * The domains come from VITE_BRAND_DOMAINS. A host that is on none of them - QA, a default
 * *.azurestaticapps.net address, localhost - gets null, and the build-time values apply.
 */
export interface BrandSite {
  /** The brand domain itself, e.g. "tab1099.com". */
  domain: string;
  /** The Tab Service portal on that domain. */
  portalWebUrl: string;
  /** This app's API on that domain. */
  apiBaseUrl: string;
}

export function parseBrandDomains(value: string | undefined): string[] {
  return (value ?? '')
    .split(',')
    .map((entry) => entry.trim().toLowerCase().replace(/^\.+|\.+$/g, ''))
    .filter(Boolean);
}

export function brandSiteFor(hostname: string, domains: readonly string[]): BrandSite | null {
  const host = hostname.trim().toLowerCase();
  for (const domain of domains) {
    if (host === domain || host.endsWith(`.${domain}`)) {
      return {
        domain,
        // Served on the portal's own host (tab1099.com, www.tab1099.com) the portal is right
        // here; on the separate efile. host it is the bare domain.
        portalWebUrl: host.startsWith('efile.') ? `https://${domain}` : `https://${host}`,
        apiBaseUrl: `https://efile-api.${domain}`,
      };
    }
  }
  return null;
}
